import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DropdownModule } from 'primeng/dropdown';
import { TagModule } from 'primeng/tag';
import { CalendarModule } from 'primeng/calendar';
import { TableModule } from 'primeng/table';
import { SidebarService } from 'src/app/core/services/sidebar.service';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CommonService } from 'src/app/shared/services/common.service';
import { RegionResponse, CountryResponse } from 'src/app/shared/model/common.model';
import { ToastModule } from 'primeng/toast';
import {
  TABLE_PARAM,
  STATUSES,
  SENT_TO_TYPE,
  USER_TYPES,
} from 'src/app/shared/constants/common.constants';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import {
  GetNotificationResponse,
  NotificationListData,
  NotificationPayload,
} from 'src/app/shared/model/notification.model';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { UserService } from 'src/app/core/services/user.service';
import { UserData, User, IOptions } from 'src/app/shared/model/user.model';
import httpErrorCodes from 'src/app/core/common/http_error_codes';
import { PaginationService } from 'src/app/shared/providers/pagination/pagination.service';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [
    CommonModule,
    PickerComponent,
    TranslateModule,
    ToastModule,
    ConfirmDialogModule,
    InputTextareaModule,
    HttpClientModule,
    CardModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    TagModule,
    CalendarModule,
    FormsModule,
    CheckboxModule,
    RouterModule,
  ],
  providers: [TranslateService, ConfirmationService],
  animations: [],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
})
export class NotificationsComponent implements OnInit {
  tableParams = { ...TABLE_PARAM };

  userTypeConstants = USER_TYPES;

  showEmojiPicker = false;

  sidebarCollapsed = false;

  noDataRow = [{}];

  first = 0;

  rows = TABLE_PARAM.PAGE_SIZE;

  sortField = 'CreatedOn';

  sortOrder = TABLE_PARAM.SORT_DEC_ORDER;

  totalRecords = 0;

  // Filters
  filters = {
    nickName: '',
    firstName: '',
    phoneNumber: '',
    regionId: '',
    countryId: '',
    dateRange: null as Date[] | null,
    paymentProfile: '',
    userStatus: '',
    withoutCountryUsers: null as boolean | null,
    withoutRegionUsers: null as boolean | null,
  };

  activeFilter = '';

  notificationPayload: NotificationPayload = {
    sendToCallers: false,
    sendToTalkingBuddies: false,
    countryId: undefined,
    title: '',
    message: '',
    hasTermsAndConditionsVersionRelease: false,
    termsAndConditionsVersion: '',
    userIds: [],
  };

  regionIds!: RegionResponse['data'];

  countryIds!: CountryResponse['data'];

  notificationData: GetNotificationResponse['data'] | null = null;

  notificationItems: NotificationListData['items'] | [] = [];

  notificationPageNumber: NotificationListData['pageNumber'] = TABLE_PARAM.PAGE_NUMBER;

  notificationPageSize: NotificationListData['pageSize'] = TABLE_PARAM.PAGE_SIZE;

  notificationTitle!: string;

  notificationMessage!: string;

  paymentProfileFilterOptions: IOptions[] = [];

  userStatusFilterOptions: IOptions[] = [];

  constructor(
    private sidebarService: SidebarService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private commonService: CommonService,
    private translateService: TranslateService,
    private toastService: ToasterService,
    private notificationService: NotificationService,
    private userService: UserService,
    private cdRef: ChangeDetectorRef,
    private paginationService: PaginationService,
  ) {}

  recipientItems: UserData['items'] | [] = [];

  recipientTotalRecords = 0;

  recipientPageNumber: UserData['pageNumber'] = TABLE_PARAM.PAGE_NUMBER;

  recipientPageSize: UserData['pageSize'] = TABLE_PARAM.PAGE_SIZE;

  recipientFirst = 0;

  recipientRows = TABLE_PARAM.PAGE_SIZE;

  selectedRecipients: User[] = [];

  formatFullName(user: { firstName: string; lastName: string }): string {
    return [user.firstName, user.lastName].filter((n) => !!n).join(' ');
  }

  getConvertedDate(utcDate: string) {
    return CommonService.getFormattedDate(utcDate);
  }

  recipientPageChange(event: { first: number; rows: number }) {
    this.recipientFirst = event.first;
    this.recipientRows = event.rows;
    this.paginationService.setPageSize('notification', this.recipientRows);
    this.recipientPageNumber = this.recipientFirst / this.recipientRows;
    this.recipientPageSize = this.recipientRows;
    this.loadRecipientsData(
      this.recipientPageNumber + 1,
      this.recipientPageSize,
      this.sortField,
      this.sortOrder,
    );
  }

  recipientSortChange(event: { field: string; order: string }) {
    this.recipientFirst = 0;
    this.recipientPageNumber = this.recipientPageNumber === 0 ? 1 : this.recipientPageNumber;
    const field = event.field === 'PaymentProfile' ? 'accountKycStatus' : event.field;
    const order =
      String(event.order) === '1' ? TABLE_PARAM.SORT_ASC_ORDER : TABLE_PARAM.SORT_DEC_ORDER;
    this.sortField = field;
    this.sortOrder = order;

    this.loadRecipientsData(this.recipientPageNumber, this.recipientPageSize, field, order);
  }

  loadRecipientsData(pageNumber: number, pageSize: number, sortField: string, sortOrder: string) {
    const formattedDates =
      this.filters?.dateRange?.map((date) => CommonService.formatDate(date?.toString())) || [];
    const isValidDate = (date: string) => date && !date.includes('NaN');
    const startDate = isValidDate(formattedDates[0]) ? formattedDates[0] : '';
    const endDate = isValidDate(formattedDates[1]) ? formattedDates[1] : '';

    let countryIdValue = (this.filters.countryId as unknown as { id: number })?.id || '';
    let regionIdValue = (this.filters.regionId as unknown as { id: number })?.id || '';

    if (countryIdValue === -1) {
      countryIdValue = '';
    }

    if (regionIdValue === -1) {
      regionIdValue = '';
    }

    const { withoutCountryUsers, withoutRegionUsers } = this.filters;

    this.userService
      .getUserFilteredData(
        {
          pageNumber,
          pageSize,
          nickName: this.filters.nickName || '',
          name: this.filters.firstName || '',
          phoneNumber: this.filters.phoneNumber || '',
          regionId: regionIdValue || '',
          countryId: countryIdValue || '',
          createdFrom: startDate,
          createdTo: endDate,
          userType: this.activeFilter || '',
          sortField,
          sortOrder,
          isExport: false,
          paymentProfileStatus: this.filters.paymentProfile || '',
          blockedUserStatus: '',
        },
        withoutCountryUsers,
        withoutRegionUsers,
      )
      .subscribe({
        next: (response) => {
          if (response.status === STATUSES.SUCCESS) {
            this.recipientItems = response.data.items;
            this.recipientTotalRecords = response.data.totalCount;
            this.cdRef.detectChanges();
          }
        },
      });
  }

  async ngOnInit(): Promise<void> {
    await this.loadTranslatedOptions();
    this.recipientRows = this.paginationService.getPageSize('notification');
    this.tableParams.ROW_PER_PAGE = this.paginationService.getAllowedSizes();
    this.recipientPageSize = this.recipientRows;
    this.recipientPageNumber = 1;

    this.sidebarService.sidebarState$.subscribe((state) => {
      this.sidebarCollapsed = state;
    });
    this.getCountries('');
    this.sortField = this.sortField === 'SendTo' ? 'SentOn' : this.sortField;
    this.loadRecipientsData(
      this.recipientPageNumber,
      this.recipientPageSize,
      this.sortField,
      this.sortOrder,
    );
  }

  loadTranslatedOptions() {
    this.paymentProfileFilterOptions = [
      {
        label: this.translateService.instant('USER_PAGE.FILTER_LABELS.COMPLETED'),
        value: 'Completed',
      },
      {
        label: this.translateService.instant('USER_PAGE.FILTER_LABELS.PENDING'),
        value: 'Pending',
      },
    ];

    this.userStatusFilterOptions = [
      {
        label: this.translateService.instant('USER_PAGE.FILTER_LABELS.ACTIVE'),
        value: 'Active',
      },
      {
        label: this.translateService.instant('USER_PAGE.FILTER_LABELS.BLOCKED'),
        value: 'Blocked',
      },
    ];
  }

  getConvertedDateTime(utcDate: string) {
    return CommonService.getFormattedDateTime(utcDate);
  }

  addEmoji(event: { emoji: { native: string } }) {
    const emoji = event.emoji.native;
    this.notificationPayload.message += emoji;
  }

  // Listen for clicks on the document
  @HostListener('document:click', ['$event.target'])
  onClickOutside(targetElement: HTMLElement) {
    const clickedInside =
      targetElement.closest('.emoji-picker') || targetElement.closest('.emoji-icon');

    if (!clickedInside) {
      this.showEmojiPicker = false;
    }
  }

  // Filter handlers (mirrors users component behavior)
  onFilterChange(type: string | null = null) {
    // Reset pagination when filter changes for notifications
    this.filters.withoutCountryUsers = null;
    this.filters.withoutRegionUsers = null;
    this.first = 0;
    this.notificationPageNumber =
      this.notificationPageNumber === 0 ? 1 : this.notificationPageNumber;

    // If filter type indicates user type change
    if (type !== null && type !== undefined) {
      this.notificationPageNumber = 1;

      if (type !== 'country') {
        this.activeFilter = type;
      } else {
        this.filters.regionId = '';
      }

      this.getCountries(this.activeFilter);

      const countryValue = (this.filters.countryId as unknown as { id: number })?.id;
      const regionValue = (this.filters.regionId as unknown as { id: number })?.id;

      if (countryValue !== undefined && countryValue !== -1) {
        this.getRegions(countryValue);
      }

      if (countryValue === -1) {
        this.filters.withoutCountryUsers = true;
      }

      if (regionValue === -1) {
        this.filters.withoutRegionUsers = true;
      }

      // Reset payment profile when switching away from talking buddies
      if (this.activeFilter !== USER_TYPES.TALKING_BUDDY) {
        this.filters.paymentProfile = '';
      }
      // non-type filter change: if activeFilter is talking buddy, ensure page resets
    } else if (this.activeFilter === USER_TYPES.TALKING_BUDDY) {
      this.notificationPageNumber = 1;
    }

    // reload recipients list based on filters and activeFilter
    this.recipientFirst = 0;
    this.recipientPageNumber = 1;
    this.loadRecipientsData(
      this.recipientPageNumber,
      this.recipientPageSize,
      this.sortField,
      this.sortOrder,
    );
  }

  applyFilters() {
    this.first = 0;
    this.notificationPageNumber =
      this.notificationPageNumber === 0 ? 1 : this.notificationPageNumber;
    this.recipientFirst = 0;
    this.recipientPageNumber = 1;
    this.loadRecipientsData(
      this.recipientPageNumber,
      this.recipientPageSize,
      this.sortField,
      this.sortOrder,
    );
  }

  onResetFilters() {
    this.filters = {
      nickName: '',
      firstName: '',
      phoneNumber: '',
      regionId: '',
      countryId: '',
      dateRange: null,
      paymentProfile: '',
      userStatus: '',
      withoutCountryUsers: null,
      withoutRegionUsers: null,
    };
    this.recipientFirst = 0;
    this.recipientPageNumber = 1;
    this.loadRecipientsData(
      this.recipientPageNumber,
      this.recipientPageSize,
      this.sortField,
      this.sortOrder,
    );
  }

  onSendToChange() {
    this.selectedRecipients = [];
    const { sendToCallers, sendToTalkingBuddies } = this.notificationPayload;

    if ((sendToCallers && sendToTalkingBuddies) || (!sendToCallers && !sendToTalkingBuddies)) {
      // both or none selected -> all users
      this.activeFilter = '';
    } else if (sendToCallers) {
      this.activeFilter = USER_TYPES.CALLER;
    } else if (sendToTalkingBuddies) {
      this.activeFilter = USER_TYPES.TALKING_BUDDY;
    }

    // reload recipients based on computed activeFilter
    this.recipientFirst = 0;
    this.recipientPageNumber = 1;
    this.loadRecipientsData(
      this.recipientPageNumber,
      this.recipientPageSize,
      this.sortField,
      this.sortOrder,
    );
  }

  redirectToCallDetailsPage(callId: string) {
    this.router.navigate(['/call-details', callId]);
  }

  getNotificationRecipient(notification: {
    sentToCallers: boolean;
    sentToTalkingBuddies: boolean;
  }): string {
    if (notification.sentToCallers && notification.sentToTalkingBuddies) {
      return SENT_TO_TYPE.ALL_USERS;
    }

    if (notification.sentToCallers) {
      return SENT_TO_TYPE.ALL_CALLERS;
    }

    if (notification.sentToTalkingBuddies) {
      return SENT_TO_TYPE.ALL_TALKING_BUDDIES;
    }

    return '';
  }

  showNotificationPopup(event: Event, notificationTitle: string, notificationMessage: string) {
    this.notificationTitle = notificationTitle;
    this.notificationMessage = notificationMessage;
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      header: notificationTitle,
      message: notificationMessage,
      blockScroll: true,
    });
  }

  closeConfirmDialog() {
    this.confirmationService.close();
  }

  updateCharacterCount(): void {
    if (this.notificationPayload.title.length > 50) {
      this.notificationPayload.title = this.notificationPayload.title.substring(0, 50);
    }

    if (this.notificationPayload.message.length > 240) {
      this.notificationPayload.message = this.notificationPayload.message.substring(0, 240);
    }
  }

  onVersionInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let { value } = input;

    // Remove any characters that are not digits or dots
    value = value.replace(/[^0-9.]/g, '');

    // Remove leading dots
    value = value.replace(/^\.+/, '');

    // Replace multiple consecutive dots with a single dot
    value = value.replace(/\.{2,}/g, '.');

    // Update the model and the input value
    this.notificationPayload.termsAndConditionsVersion = value;
    input.value = value;
  }

  getCountries(userType: string) {
    this.commonService.getCountry(userType).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.countryIds = response.data;
          this.countryIds = [
            ...this.countryIds,
            {
              id: -1,
              countryName: 'Without Location',
              phoneCode: '',
              countryShortCode: '',
            },
          ];
        }
      },
    });
  }

  getRegions(countryIds: number) {
    this.commonService.getRegions([countryIds]).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.regionIds = response.data;
          this.regionIds = [
            ...this.regionIds,
            {
              id: -1,
              regionName: 'Without Location',
              countryId: -1,
            },
          ];
        }
      },
    });
  }

  onCountryChangeGetStates(selectedCountry: string) {
    const countyValue = (
      selectedCountry as unknown as {
        id: number;
        countryName: string;
        phoneCode: number;
      }
    ).id;
    this.getRegions(countyValue);
  }

  onSendNotification() {
    if (this.notificationPayload.countryId !== undefined) {
      const countryIdValue = (
        this.notificationPayload.countryId as unknown as {
          id: number;
          countryName: string;
          phoneCode: number;
        }
      ).id;
      this.notificationPayload.countryId = countryIdValue;
    }

    if (!this.notificationPayload.title && !this.notificationPayload.message) {
      this.toastService.showErrorToast(
        this.translateService.instant('NOTIFICATION_PAGE.TOAST_MESSAGE.ERROR_TITLE_MESSAGE'),
      );
      return;
    }

    if (!this.notificationPayload.title) {
      this.toastService.showErrorToast(
        this.translateService.instant('NOTIFICATION_PAGE.TOAST_MESSAGE.TITLE_ERROR_TITLE_MESSAGE'),
      );
      return;
    }

    if (!this.notificationPayload.message) {
      this.toastService.showErrorToast(
        this.translateService.instant(
          'NOTIFICATION_PAGE.TOAST_MESSAGE.MESSAGE_ERROR_TITLE_MESSAGE',
        ),
      );
      return;
    }

    if (
      this.notificationPayload.sendToCallers === false &&
      this.notificationPayload.sendToTalkingBuddies === false &&
      this.selectedRecipients.length === 0
    ) {
      this.toastService.showErrorToast(
        this.translateService.instant('NOTIFICATION_PAGE.TOAST_MESSAGE.ERROR_SELECT_SENT_TO'),
      );
      return;
    }

    if (
      this.notificationPayload.hasTermsAndConditionsVersionRelease &&
      !this.notificationPayload.termsAndConditionsVersion
    ) {
      this.toastService.showErrorToast(
        this.translateService.instant('NOTIFICATION_PAGE.TOAST_MESSAGE.ERROR_TC_VERSION'),
      );
      return;
    }

    if (this.selectedRecipients.length > 0) {
      const userIds = this.selectedRecipients.map((user) => user.id);
      this.notificationPayload.userIds = userIds;
    }

    const countryIdValue = (this.filters.countryId as unknown as { id: number }).id;

    if (this.filters.countryId && countryIdValue !== -1) {
      this.notificationPayload.countryId = countryIdValue;
    }

    this.notificationService.sendNotification(this.notificationPayload).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.toastService.showSuccessToast(
            this.translateService.instant(
              'NOTIFICATION_PAGE.TOAST_MESSAGE.SUCCESS_SENT_NOTIFICATION',
            ),
          );
          this.resetForm();
          this.selectedRecipients = [];
          this.first = 0;
          this.notificationPageNumber =
            this.notificationPageNumber === 0 ? 1 : this.notificationPageNumber;
          this.sortField = this.sortField === 'SendTo' ? 'SentOn' : this.sortField;
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            err?.error?.Message ||
              this.translateService.instant(
                'NOTIFICATION_PAGE.TOAST_MESSAGE.FAILED_SEND_NOTIFICATION',
              ),
          );
        }
      },
    });
  }

  private resetForm() {
    this.notificationPayload = {
      sendToCallers: false,
      sendToTalkingBuddies: false,
      countryId: undefined,
      title: '',
      message: '',
      hasTermsAndConditionsVersionRelease: false,
      termsAndConditionsVersion: '',
    };
  }
}
